/**
 * Saving a file the API sent: the name comes from the server's
 * `Content-Disposition` (which carries the real, possibly German, name as
 * RFC 5987 `filename*`), with a fallback for a response that has none.
 */

/** The file name in a `Content-Disposition` header, or null. */
export function filenameFromDisposition(header: string | null): string | null {
    if (!header) return null
    const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)
    if (encoded) {
        try {
            return decodeURIComponent(encoded[1].trim())
        } catch {
            // A malformed escape: fall through to the plain name.
        }
    }
    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header)
    return plain ? plain[1].trim() : null
}

/** Hand `blob` to the browser as a download named `filename`. */
export function saveBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    // Revoke after the click has been handled; revoking at once can cancel
    // the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 0)
}

/** The `detail` of a FastAPI error response, if it has a readable one. */
export async function errorDetail(response: Response): Promise<string | null> {
    try {
        const body: unknown = await response.json()
        if (body && typeof body === 'object' && 'detail' in body) {
            const detail = (body as { detail: unknown }).detail
            if (typeof detail === 'string' && detail) return detail
        }
    } catch {
        // Not JSON: the caller's own message is shown instead.
    }
    return null
}
